import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import { Tooltip } from '@zennolab_platform/webui-kit/dist/ui';
import {
  Alert,
  ButtonANTD,
  CheckboxANTD,
  InputANTD,
  ModalANTD,
} from '@zennolab_platform/webui-kit/dist/ui/antdComponents';
import { useFormatMessage } from '@zennolab_platform/webui-sdk/dist/hooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useId } from 'react';
import { useIntl } from 'react-intl';
import { Container } from 'typedi';

import { VersionCardController } from '../../controllers/VersionCardController';
import { useErrorText } from '../../hooks/useErrorText';
import { useMountInit } from '../../hooks/useMountInit';
import { useRedirect } from '../../hooks/useRedirect';
import { useValidationText } from '../../hooks/useValidationText';
import { VersionCardStore } from '../../stores/VersionCardStore';
import { AnnouncementType, type IAnnouncementEntity } from '../../types/announcement';
import { Fieldset, FormHeader, HintRow, LocalizationsEditor, Muted } from '../formParts';
import { ActionBar, Page, Toolbar } from '../layout';
import { FormColumns, FormFields, PreviewPanel } from '../Preview';

/** New version card (no `card`) or an existing one. Draft, scheduled and published cards are editable (texts, Important and pin),
 * the version stays locked once the card exists; withdrawn and expired cards are read-only. */
export const VersionCardForm = observer(function VersionCardForm({ card: initial }: { card?: IAnnouncementEntity }) {
  const store = Container.get(VersionCardStore);
  const controller = Container.get(VersionCardController);
  const intl = useIntl();
  const { t } = useFormatMessage();
  const errorText = useErrorText();
  const validationText = useValidationText();
  const versionHintId = useId();
  const versionFieldHintId = useId();
  const importantHintId = useId();
  const init = useCallback(() => store.init(initial), [store, initial]);
  const ready = useMountInit(init);
  useRedirect(store.redirect, store.clearRedirect);

  if (!ready) return null;
  const { card, version, pinned, important, localizations, publishAt, busy, serverError, confirming, notifyAgain } =
    store;
  const { scheduled, published, readOnly, reason, time } = store;
  const heading = card
    ? t(
        {
          id: 'announcements.versionCard.title',
          defaultMessage: 'Карточка версии {version}',
          description: 'Заголовок формы карточки версии',
        },
        { version: card.businessVersion ?? '' },
      )
    : t({
        id: 'announcements.versionCard.newTitle',
        defaultMessage: 'Новая карточка версии',
        description: 'Заголовок формы новой карточки версии',
      });
  const importantHint = t({
    id: 'announcements.form.importantHint',
    defaultMessage:
      'Анонс сразу появится во всплывающем уведомлении в ZennoBrowser, а в ленте получит метку «Важное». Используйте для срочного: техработы, сбои, важные изменения.',
    description: 'Подсказка к флажку «Важное»',
  });
  const versionHint = t({
    id: 'announcements.versionCard.versionHint',
    defaultMessage:
      'Номер версии ZennoBrowser (как в «О программе»), к которой относится карточка. Пользователь увидит её, когда проверка обновлений предложит ему эту версию, а после установки — как «Что нового».',
    description: 'Подсказка к полю версии карточки',
  });
  const publishAtLabel = t({
    id: 'announcements.versionCard.publishAt',
    defaultMessage: 'Время публикации',
    description: 'Поле формы карточки версии',
  });
  const publishNowLabel = t({
    id: 'announcements.versionCard.publishNow',
    defaultMessage: 'Опубликовать сейчас',
    description: 'Кнопка публикации карточки версии и подтверждения',
  });
  const saveLabel = t({ id: 'announcements.form.save', defaultMessage: 'Сохранить', description: 'Кнопка сохранения' });
  const withdrawLabel = t({
    id: 'announcements.form.withdraw',
    defaultMessage: 'Снять с публикации',
    description: 'Кнопка снятия с публикации и подтверждения',
  });
  const cancelLabel = t({ id: 'announcements.form.cancel', defaultMessage: 'Отмена', description: 'Кнопка отмены' });

  return (
    <Page>
      <FormHeader heading={heading} status={card?.status} />
      {time && (
        <p>
          {t(
            {
              id: 'announcements.versionCard.time',
              defaultMessage: 'Публикация: {time}',
              description: 'Время публикации карточки версии',
            },
            { time: intl.formatDate(time, { dateStyle: 'short', timeStyle: 'short' }) },
          )}
        </p>
      )}
      {readOnly && (
        <Alert
          type="info"
          title={t({
            id: 'announcements.versionCard.readOnly',
            defaultMessage: 'Карточка снята с публикации или истекла, редактирование недоступно.',
            description: 'Сообщение над формой завершённой карточки версии',
          })}
        />
      )}

      <FormColumns>
        <FormFields>
          <Fieldset>
            <label>
              <HintRow>
                {t({
                  id: 'announcements.versionCard.version',
                  defaultMessage: 'Версия ZennoBrowser',
                  description: 'Поле формы карточки версии',
                })}
                <Tooltip title={versionHint} trigger={['hover', 'focus']}>
                  <QuestionCircleOutlined id={versionHintId} tabIndex={0} aria-label={versionHint} />
                </Tooltip>
              </HintRow>
              <InputANTD
                disabled={card !== undefined}
                placeholder="1.22.0"
                aria-describedby={`${versionHintId} ${versionFieldHintId}`}
                value={version}
                onChange={e => store.setVersion(e.target.value)}
              />
            </label>
            <Muted id={versionFieldHintId}>
              {t({
                id: 'announcements.version.fieldHint',
                defaultMessage: 'Версия ZennoBrowser — как в «Настройки → Версия программы», например 1.22.0',
                description: 'Подсказка под полем версии',
              })}
            </Muted>
          </Fieldset>

          <Fieldset>
            <legend>
              {t({ id: 'announcements.form.options', defaultMessage: 'Параметры', description: 'Раздел формы анонса' })}
            </legend>
            {/* The hint sits next to the checkbox, not in its label, so the box keeps the name "Важное". */}
            <HintRow>
              <CheckboxANTD
                disabled={readOnly}
                checked={important}
                aria-describedby={importantHintId}
                onChange={e => store.setImportant(e.target.checked)}
              >
                {t({
                  id: 'announcements.form.important',
                  defaultMessage: 'Важное',
                  description: 'Флажок «Важное» в форме анонса',
                })}
              </CheckboxANTD>
              <Tooltip title={importantHint} trigger={['hover', 'focus']}>
                <QuestionCircleOutlined id={importantHintId} tabIndex={0} aria-label={importantHint} />
              </Tooltip>
            </HintRow>
            <CheckboxANTD disabled={readOnly} checked={pinned} onChange={e => store.setPinned(e.target.checked)}>
              {t({
                id: 'announcements.form.pinned',
                defaultMessage: 'Закрепить',
                description: 'Флажок «Закрепить» в форме анонса',
              })}
            </CheckboxANTD>
          </Fieldset>

          <LocalizationsEditor readOnly={readOnly} value={localizations} onChange={store.setLocalizations} />
        </FormFields>
        <PreviewPanel
          localizations={localizations}
          type={card?.type ?? AnnouncementType.ANNOUNCEMENT_TYPE_UPDATE}
          businessVersion={version.trim()}
          pinned={pinned}
          important={important}
        />
      </FormColumns>

      {!readOnly && (
        <ActionBar>
          {reason && <Alert type="warning" title={validationText(reason)} />}
          {serverError && <Alert type="error" title={errorText(serverError)} />}
          {published || scheduled ? (
            <Toolbar>
              {scheduled && (
                <label>
                  {publishAtLabel}{' '}
                  <InputANTD
                    type="datetime-local"
                    value={publishAt}
                    onChange={e => store.setPublishAt(e.target.value)}
                  />
                </label>
              )}
              <ButtonANTD
                type="primary"
                disabled={busy || reason !== null || (scheduled && !publishAt)}
                onClick={() => void controller.saveLive()}
              >
                {saveLabel}
              </ButtonANTD>
              <ButtonANTD danger disabled={busy} onClick={() => store.setConfirming('withdraw')}>
                {withdrawLabel}
              </ButtonANTD>
            </Toolbar>
          ) : (
            <>
              <Toolbar>
                <ButtonANTD disabled={busy || reason !== null} onClick={() => void controller.saveDraft()}>
                  {t({
                    id: 'announcements.form.saveDraft',
                    defaultMessage: 'Сохранить черновик',
                    description: 'Кнопка сохранения черновика анонса',
                  })}
                </ButtonANTD>
                <ButtonANTD
                  type="primary"
                  disabled={busy || reason !== null}
                  onClick={() => store.setConfirming('publish')}
                >
                  {publishNowLabel}
                </ButtonANTD>
              </Toolbar>
              <Toolbar>
                <label>
                  {publishAtLabel}{' '}
                  <InputANTD
                    type="datetime-local"
                    value={publishAt}
                    onChange={e => store.setPublishAt(e.target.value)}
                  />
                </label>
                <ButtonANTD disabled={busy || reason !== null || !publishAt} onClick={() => void controller.schedule()}>
                  {t({
                    id: 'announcements.versionCard.schedule',
                    defaultMessage: 'Запланировать',
                    description: 'Кнопка планирования публикации карточки версии',
                  })}
                </ButtonANTD>
              </Toolbar>
            </>
          )}
          <ModalANTD
            open={confirming === 'publish'}
            title={t({
              id: 'announcements.versionCard.publishConfirmTitle',
              defaultMessage: 'Опубликовать карточку версии?',
              description: 'Заголовок подтверждения публикации карточки версии',
            })}
            okText={publishNowLabel}
            cancelText={cancelLabel}
            onOk={() => void controller.publish()}
            onCancel={() => store.setConfirming(null)}
          >
            {t(
              {
                id: 'announcements.versionCard.publishConfirmText',
                defaultMessage:
                  'Карточку увидят пользователи ZennoBrowser версий до {version} включительно, а на версии {version} — как «Что нового».',
                description: 'Текст подтверждения публикации карточки версии; version — номер версии',
              },
              { version: version.trim() },
            )}
          </ModalANTD>
          {card && (
            <>
              <ModalANTD
                open={confirming === 'edit'}
                title={t({
                  id: 'announcements.versionCard.editConfirmTitle',
                  defaultMessage: 'Сохранить изменения карточки?',
                  description: 'Заголовок подтверждения сохранения карточки версии',
                })}
                okText={saveLabel}
                cancelText={cancelLabel}
                onOk={() => void controller.confirmEdit()}
                onCancel={() => store.setConfirming(null)}
              >
                <p>
                  {t({
                    id: 'announcements.versionCard.editConfirmText',
                    defaultMessage:
                      'Пользователи увидят новый текст, прочитанная карточка останется прочитанной. Отметьте «Уведомить снова», чтобы она снова стала непрочитанной у всех.',
                    description: 'Текст подтверждения сохранения карточки версии',
                  })}
                </p>
                <CheckboxANTD checked={notifyAgain} onChange={e => store.setNotifyAgain(e.target.checked)}>
                  {t({
                    id: 'announcements.form.notifyAgain',
                    defaultMessage: 'Уведомить снова',
                    description: 'Флажок при сохранении опубликованного анонса или карточки версии',
                  })}
                </CheckboxANTD>
              </ModalANTD>
              <ModalANTD
                open={confirming === 'withdraw'}
                title={t({
                  id: 'announcements.versionCard.withdrawConfirmTitle',
                  defaultMessage: 'Снять карточку версии с публикации?',
                  description: 'Заголовок подтверждения снятия карточки версии с публикации',
                })}
                okText={withdrawLabel}
                okButtonProps={{ danger: true }}
                cancelText={cancelLabel}
                onOk={() => void controller.confirmWithdraw()}
                onCancel={() => store.setConfirming(null)}
              >
                {t({
                  id: 'announcements.versionCard.withdrawConfirmText',
                  defaultMessage: 'Карточка исчезнет из ленты всех пользователей. Вернуть её нельзя.',
                  description: 'Текст подтверждения снятия карточки версии с публикации',
                })}
              </ModalANTD>
            </>
          )}
        </ActionBar>
      )}
    </Page>
  );
});
